import moment from 'moment'
import { path, find, propEq } from 'ramda'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Avatar } from '@/components/avatar'
import {
  StyledTableCell,
  Accordion,
  AccordionContent,
  AccordionTableRow,
  AccordionHeaderText,
  AccordionHeader,
  MobileRowWrapper,
  MobileUnitDetails,
  CellText
} from '@/components/showings-table'
import useMediaQuery from '@/hooks/use-media-query'
import { select } from '@/store'
import { colors, breakpoints } from '@/styles'
import toBoolean from '@/utils/to-boolean'
import { showAttendeesModal } from './attendees-modal'
import AttendeeInfo from './attendee-info'

const AvatarsWrapper = styled.div`
  display: flex;
  padding: 10px 20px 20px;
  height: 118px;
`

const AdditionalUsers = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 10px;
`

const AdditionalAvatarsWrapper = styled.div`
  display: flex;

  > div:first-of-type {
    z-index: 1;
    box-shadow: 2px 0px 2px 1px rgba(0, 0, 0, 0.15);
  }
`

const AttendeeName = styled.p`
  color: ${colors.regular};
  font-size: 13px;
  letter-spacing: -0.32px;
  line-height: 22px;
  text-align: center;
  margin: 5px 0 0;
  max-width: 70px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`

const ShowingRowComponent = ({ showing, isLastRow, listingId, currentTab }) => {
  const { t } = useTranslation()
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.tabletLandscape})`)
  const displaySmallDesktop = useMediaQuery(`(max-width: 1000px)`)
  const displayDesktop = useMediaQuery(`(max-width: ${breakpoints.desktop})`)

  const attendeesIds = useSelector(
    path(['listing', listingId, 'listing_appointments', showing.id, 'users'])
  )
  const attendeesAvatars = useSelector(state =>
    select.listing.attendees(state, {
      listingId,
      attendeesIds
    })
  )
  const showingAttendees =
    attendeesIds?.map(attendee => find(propEq('id', attendee), attendeesAvatars)) || []

  const getMeetingTime = () => {
    const meetingTime = showing.last_snooze_time || showing.meeting_at
    return moment(meetingTime).format('h:mm a')
  }

  const getMaxAvatars = () => {
    if (displaySmallDesktop) return 2
    if (displayDesktop) return 3
    return 5
  }

  const getAttendeeAvatars = () => {
    const maxAvatars = getMaxAvatars()
    const avatarCount = showingAttendees.length

    if (avatarCount === 0) return <AvatarsWrapper></AvatarsWrapper>

    const extraAvatars = avatarCount - maxAvatars
    const showAdditional = extraAvatars > 1
    const filteredUsers = showingAttendees.slice(0, showAdditional ? maxAvatars : maxAvatars + 1)

    return (
      <AvatarsWrapper>
        {filteredUsers.map(tenant => (
          <AttendeeInfo
            key={`${tenant.id}-${showing.meeting_at}`}
            tenant={tenant}
            listingId={listingId}
          />
        ))}
        {showAdditional && (
          <AdditionalUsers>
            <AdditionalAvatarsWrapper>
              <Avatar width={50} height={50} shadow />
              <Avatar width={50} height={50} left={-25} shadow />
            </AdditionalAvatarsWrapper>
            <AttendeeName>
              <Trans
                i18nKey="b.listing.view.additional_users.text"
                values={{ userCount: extraAvatars }}
                defaults="+ {{userCount }} more..."
              />
            </AttendeeName>
          </AdditionalUsers>
        )}
      </AvatarsWrapper>
    )
  }

  const MobileShowingRow = ({ displayGaps }) => {
    const attendeeCount = showing.user_count

    return (
      <>
        <StyledTableCell gapLeft={displayGaps}>
          <MobileRowWrapper indent>
            <MobileUnitDetails top="0">
              <p>{getMeetingTime(showing)}</p>
              <p>
                <Trans i18nKey="b.listing.view.showing_duration.text">
                  {{ duration: showing.duration }} min.
                </Trans>
              </p>
              <p>
                <Trans i18nKey="b.listing.view.list_count_attendees.text" count={attendeeCount}>
                  Attendees: {{ attendeeCount }}
                </Trans>
              </p>
            </MobileUnitDetails>
          </MobileRowWrapper>
        </StyledTableCell>
        <StyledTableCell gapRight={displayGaps}>
          <CellText lastCell bottom="0">
            {t('l.listing.list.view.text', 'View')}
          </CellText>
        </StyledTableCell>
      </>
    )
  }

  const ShowingRow = () => {
    const attendeeCount = showing.user_count
    return (
      <>
        <StyledTableCell>
          <CellText firstCell>{getMeetingTime(showing)}</CellText>
        </StyledTableCell>
        <StyledTableCell>
          <CellText>
            <strong>
              <Trans i18nKey="b.listing.view.showing_duration.text">
                {{ duration: showing.duration }} min.
              </Trans>
            </strong>
          </CellText>
        </StyledTableCell>
        <StyledTableCell>
          <CellText>
            <Trans i18nKey="b.listing.view.count_attendees.text" count={attendeeCount}>
              {{ attendeeCount }} attendees
            </Trans>
          </CellText>
        </StyledTableCell>
        <StyledTableCell>{getAttendeeAvatars(showing)}</StyledTableCell>
        <StyledTableCell>
          <CellText lastCell>{t('l.listing.list.view.text', 'View')}</CellText>
        </StyledTableCell>
      </>
    )
  }

  return (
    <AccordionTableRow
      onClick={() =>
        showAttendeesModal({
          title: t('l.showings.view.attendees_modal.title.text', 'Showing Info'),
          showingAttendees,
          listingId,
          appointmentId: showing.id,
          lastSnoozeTime: showing.last_snooze_time,
          duration: showing.duration,
          meetingAt: showing.meeting_at,
          currentTab,
          hidden: toBoolean(showing.hidden)
        })
      }
    >
      {displayTablet ? (
        <MobileShowingRow displayGaps={!isLastRow} />
      ) : (
        <ShowingRow displayGaps={!isLastRow} />
      )}
    </AccordionTableRow>
  )
}

const ShowingsContainer = ({ listingId, showingsDate, currentTab, expanded }) => {
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  const date = showingsDate[0].meeting_at
  const formattedDate = moment(date).format('dddd - MMM. DD, YYYY')
  const showingCount = showingsDate.length

  const getTitle = () => {
    return (
      <AccordionHeaderText>
        <span>
          {formattedDate}{' '}
          {!displayMobile && (
            <Trans i18nKey="b.listing.view.count_slots.text" count={showingCount}>
              ({{ showingCount }} slots)
            </Trans>
          )}
        </span>
      </AccordionHeaderText>
    )
  }

  return (
    <Accordion expanded={expanded}>
      <AccordionHeader colSpan={8} unitCount={showingsDate.length}>
        {getTitle()}
      </AccordionHeader>
      <AccordionContent>
        {showingsDate.map((showing, index) => {
          const isLastRow = index + 1 === showingCount

          return (
            <ShowingRowComponent
              key={showing.id}
              showing={showing}
              isLastRow={isLastRow}
              listingId={listingId}
              currentTab={currentTab}
            />
          )
        })}
      </AccordionContent>
    </Accordion>
  )
}

export default ShowingsContainer
